import React from "react";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import VisibilityChip from "./VisibilityChip";

/**
 * WorkingJournalStream — the provisional Working Journal section of the
 * Reflection Canvas article. Extracted verbatim from
 * `ReflectionCanvasArticle.jsx` during the final Embrace hardening pass.
 *
 * Renders in two halves:
 *   · Left rail — § Working Journal metadata (count + intent copy).
 *   · Right — brass-anchored composer (Textarea, visibility chip, save
 *     button, promote-to-current shortcut, briefing-studio jump-off)
 *     plus a chronological stream of prior entries. Promoted entries
 *     fade to 50% opacity; each entry shows author initials, timestamp,
 *     visibility chip, and — if not yet promoted — a "Promote ↑" link.
 *
 * Behaviour, endpoints, styling, and every `data-testid` are preserved
 * unchanged from the pre-extraction inline version. The parent still
 * owns all state and network calls; this component is purely the view.
 */
const authorInitials = (name) => {
  if (!name) return "??";
  const parts = String(name).split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export default function WorkingJournalStream({
  columnMode = false,
  testidBase,
  pinnedNodeKey,
  isMovement,
  reflections,
  currentUser,
  jotDraft,
  setJotDraft,
  jotVisibility,
  setJotVisibility,
  savingJot,
  onSaveJot,
  onJotKeyDown,
  onCycleVisibility,
  onPromoteFromDraft,
  onPromoteEntry,
  onDeleteEntry,
  onOpenBriefing,
}) {
  // Same discipline as CurrentReflectionBlock — every ancestor of the
  // journal-entry body needs `min-w-0` for `overflow-wrap: anywhere`
  // on the body to actually contain a rogue no-space paste.
  //
  // Feb 2026 · Iter 54 — In columnMode this column becomes an
  // independently scrollable reading pane inside the bounded canvas
  // grid: `md:h-full md:min-h-0 md:overflow-y-auto`. Mobile keeps
  // natural stacked flow. Long journal streams no longer push the
  // Signals section below the fold.
  const outerCls = columnMode
    ? "px-6 md:px-10 py-8 md:py-10 min-w-0 md:h-full md:min-h-0 md:overflow-y-auto md:overscroll-contain"
    : "border-t border-dashed border-border px-5 md:px-8 py-6 md:py-7 grid grid-cols-1 md:grid-cols-[180px_1fr] gap-6 min-w-0";
  return (
    <div className={outerCls} data-testid={`${testidBase}-journal`}>
      {columnMode && (
        <div className="border-b border-brass pb-3 mb-6 flex items-baseline justify-between gap-4 flex-wrap">
          <div className="text-[0.6875rem] font-mono uppercase tracking-[0.28em] text-brass flex items-center gap-2">
            <span>§ Working Journal</span>
            <span className="text-border">·</span>
            <span>Provisional</span>
          </div>
          <div className="text-[0.8125rem] italic text-muted-foreground" style={{ fontFamily: "'Merriweather', serif" }}>
            Doubts · questions · first impressions
          </div>
        </div>
      )}
      {!columnMode && (
      <div>
        <div className="text-[0.625rem] font-mono uppercase tracking-[0.24em] text-brass">§ Working Journal</div>
        <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mt-1.5 leading-relaxed">
          {reflections.length} entr{reflections.length === 1 ? "y" : "ies"}
        </div>
        <div className="text-xs italic text-muted-foreground mt-3 leading-relaxed" style={{ fontFamily: "'Merriweather', serif" }}>
          Provisional thinking. Doubts, first impressions, unresolved tensions. Cheap to write, easy to promote.
        </div>
      </div>
      )}
      <div>
        {/* Composer */}
        <div className="pl-4 border-l border-border/70 relative">
          <span className="absolute left-[-1px] top-2 w-[3px] h-6 bg-brass" aria-hidden />
          <Textarea
            value={jotDraft}
            onChange={(e) => setJotDraft(e.target.value)}
            onKeyDown={onJotKeyDown}
            placeholder="Write a working thought — a doubt, a hunch, a question you're carrying…"
            data-testid={`${testidBase}-journal-textarea`}
            className="min-h-[80px] rounded-none bg-transparent border-none focus-visible:ring-0 italic text-base p-0"
            style={{ fontFamily: "'Cormorant Garamond', 'Merriweather', serif", fontWeight: 500 }}
          />
          <div className="flex flex-wrap items-center gap-4 mt-3 pt-3 border-t border-dashed border-border/70">
            <VisibilityChip
              vis={jotVisibility}
              onCycle={() => {
                const order = ["private", "team", "firm"];
                setJotVisibility(order[(order.indexOf(jotVisibility) + 1) % order.length]);
              }}
              dataTestId={`${testidBase}-journal-visibility`}
            />
            <button
              type="button"
              onClick={onSaveJot}
              disabled={savingJot || !jotDraft.trim()}
              data-testid={`${testidBase}-journal-save-btn`}
              className="text-[0.6875rem] font-mono uppercase tracking-widest text-brass border-b border-brass pb-1 hover:text-foreground hover:border-foreground disabled:opacity-40 disabled:hover:text-brass disabled:hover:border-brass"
            >
              {savingJot ? "Saving…" : "Save to journal"}
            </button>
            <button
              type="button"
              onClick={() => {
                if (!jotDraft.trim()) { toast.error("Write something first, then promote."); return; }
                onPromoteFromDraft(jotDraft);
              }}
              className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground hover:text-primary"
            >
              Promote to current reflection ↑
            </button>
            <button
              type="button"
              onClick={() => onOpenBriefing(isMovement ? null : pinnedNodeKey)}
              data-testid={`pinned-briefing-${pinnedNodeKey}`}
              className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground hover:text-primary"
            >
              Capture in Advisory Desk →
            </button>
          </div>
        </div>

        {/* Prior entries stream — capped viewport.
            Up to 3 entries render inline; when the stream exceeds 3,
            it wraps in a stable max-height scroll region so the
            composer above remains easily accessible and the whole
            article page never grows without bound. Newest-first
            ordering is preserved (reflections is stored oldest-first
            by the backend, .slice().reverse() gives us newest at top).
            All prior visibility, promotion, author, timestamp, and
            movement-attribution behaviour is unchanged. */}
        {reflections.length > 0 && (() => {
          const ordered = reflections.slice().reverse();
          const isCapped = ordered.length > 3;
          return (
            <div className="mt-6" data-testid={`${testidBase}-journal-entries-wrap`}>
              {isCapped && (
                <div className="flex items-baseline justify-between mb-2 gap-3">
                  <div className="text-[0.5938rem] font-mono uppercase tracking-widest text-muted-foreground">
                    § Journal · newest first
                  </div>
                  <div className="text-[0.5938rem] font-mono uppercase tracking-widest text-muted-foreground">
                    {ordered.length} entries · newest 3 shown, scroll for older
                  </div>
                </div>
              )}
              <div
                className={`space-y-3 ${isCapped ? "max-h-[28rem] overflow-y-auto overscroll-contain pr-2" : ""}`}
                data-testid={`${testidBase}-journal-entries`}
                style={isCapped ? { WebkitOverflowScrolling: "touch" } : undefined}
              >
                {ordered.map((r) => {
                  const isPromoted = r.status === "promoted";
                  const isAuthor = r.author_email && r.author_email === currentUser?.email;
                  return (
                    <div
                      key={r.id}
                      className={`pl-4 border-l border-border/70 relative py-2 ${isPromoted ? "opacity-50" : ""}`}
                      data-testid={`${testidBase}-journal-entry-${r.id}`}
                    >
                      <div className="flex flex-wrap items-center gap-2 text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
                        <span
                          className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-secondary border border-border text-[0.5625rem] text-foreground"
                          style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700 }}
                          aria-hidden
                        >
                          {authorInitials(r.author_name)}
                        </span>
                        <span>{r.author_name || "Unknown"}</span>
                        <span className="text-border">·</span>
                        <span>{r.created_at ? new Date(r.created_at).toLocaleString() : ""}</span>
                        <VisibilityChip
                          vis={r.visibility}
                          onCycle={() => { if (isAuthor) onCycleVisibility(r.id, r.visibility); else toast.error("Only the author can change visibility."); }}
                          dataTestId={`${testidBase}-entry-vis-${r.id}`}
                        />
                        {isPromoted && <span className="text-brass">§ promoted</span>}
                      </div>
                      <div
                        className="mt-1.5 text-sm leading-relaxed"
                        style={{
                          fontFamily: "'Cormorant Garamond', 'Merriweather', serif",
                          fontStyle: "italic",
                          fontWeight: 500,
                          fontSize: "1.0625rem",
                          // Journal entries render the same user-authored
                          // reflection text as Current Reflection — apply
                          // the same wrap discipline so URLs, pasted
                          // content, and long no-space tokens never push
                          // the right column wider than its track.
                          overflowWrap: "anywhere",
                          wordBreak: "break-word",
                          minWidth: 0,
                        }}
                        data-testid={`${testidBase}-entry-body-${r.id}`}
                      >
                        {r.response}
                      </div>
                      <div className="flex items-center gap-4 mt-1.5">
                        {!isPromoted && (
                          <button
                            type="button"
                            onClick={() => onPromoteEntry(r.id, r.response)}
                            data-testid={`${testidBase}-entry-promote-${r.id}`}
                            className="text-[0.625rem] font-mono uppercase tracking-widest text-brass hover:text-foreground"
                          >
                            Promote ↑
                          </button>
                        )}
                        {isAuthor && onDeleteEntry && (
                          <button
                            type="button"
                            onClick={() => onDeleteEntry(r.id)}
                            data-testid={`${testidBase}-entry-delete-${r.id}`}
                            className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground hover:text-destructive"
                          >
                            Delete ×
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}
